// src/services/contentService.ts - MINIMAL FIX FOR YOUR CURRENT SETUP
import type { ContentItem } from '../types';
import { config } from '../config';

const API_BASE_URL = config.apiBaseUrl;

// Import unified auth for secure token management
import unifiedAuth from '../utils/unifiedAuth';

// Helper function for API requests with better error handling.
// Token attachment + 401 refresh-retry is delegated to unifiedAuth.fetch()
// (the single shared implementation) - this keeps only this service's own
// error-shape handling.
const apiRequest = async (endpoint: string, options: RequestInit = {}) => {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const response = await unifiedAuth.fetch(`${API_BASE_URL}${endpoint}`, {
    headers,
    ...options,
  });

  if (!response.ok) {
    const _errorData = await response.json().catch(() => ({}));

    // Handle authentication errors (still 401 after unifiedAuth.fetch already
    // attempted a refresh-then-retry, so the session really is unusable)
    if (response.status === 401) {
      // Clear tokens and redirect (matches your existing auth flow)
      unifiedAuth.clearTokens();
      window.location.href = '/login';
      throw new Error('Your session has expired. Please log in again.');
    }

    throw new Error(_errorData.message || _errorData.detail || `HTTP ${response.status}: ${response.statusText}`);
  }

  const data = await response.json();

  return data;
};

// Helper function to map backend content type to frontend type
const mapContentType = (backendType: string): ContentItem['type'] => {
  const typeMap: Record<string, ContentItem['type']> = {
    'cinema': 'cinema',
    'content': 'content',
    'podcast': 'podcast',
    'animation': 'animation',
    'sneak_peek': 'sneak-peek',
    'sneak-peek': 'sneak-peek',
    'live_video': 'live-video',
    'live-video': 'live-video',
  };
  return typeMap[backendType] || 'content';
};

export const contentService = {
  // User Library Functions
  async getUserLibrary(): Promise<ContentItem[]> {
    const response = await apiRequest('/user/library/');
    
    // Backend now returns a simple array of watched content items
    const items = response.data || [];
    
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return items.map((item: Record<string, any>) => ({
      id: item.id,
      title: item.title || '',
      description: item.description || '',
      thumbnail: item.thumbnail || '',
      type: mapContentType(item.type),
      duration: item.duration || 0,
      views: item.viewCount || 0,
      likes: item.likeCount || 0,
      tags: item.tags || [],
      createdAt: item.createdAt,
      watchProgress: item.watchProgress || 0,
      isFavorited: item.isLiked || false,
      isBookmarked: item.isBookmarked || false,
    }));
  },

  // User Favorites Functions
  async getUserFavorites(): Promise<ContentItem[]> {
    try {
      const response = await apiRequest('/user/favorites/');
      
      // Backend now returns a simple array of liked content items
      const items = response.data || [];
      
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return items.map((item: Record<string, any>) => ({
        id: item.id,
        title: item.title || '',
        description: item.description || '',
        thumbnail: item.thumbnail || '',
        type: mapContentType(item.type),
        duration: item.duration || 0,
        views: item.viewCount || 0,
        likes: item.likeCount || 0,
        tags: item.tags || [],
        createdAt: item.createdAt,
        watchProgress: item.watchProgress || 0,
        isFavorited: true, // Always true for favorites
        isBookmarked: item.isBookmarked || false,
      }));
    } catch (error) {
      console.error('Error fetching user favorites:', error);
      throw error;
    }
  },

  // Favorite/Unfavorite Content
  async favoriteContent(contentType: string, contentId: string): Promise<void> {
    try {
      const typeMap: Record<string, string> = {
        'story': 'originals',
        'cinema': 'media/cinema',
        'content': 'media/content',
        'podcast': 'podcasts',
        'animation': 'animations',
        'sneak-peek': 'sneak-peeks'
      };

      const endpoint = typeMap[contentType];
      if (!endpoint) {
        throw new Error('Invalid content type');
      }

      await apiRequest(`/${endpoint}/${contentId}/favorite/`, {
        method: 'POST',
      });
    } catch (error) {
      throw error;
    }
  },

  // Bookmark/Unbookmark Content
  async bookmarkContent(contentType: string, contentId: string): Promise<void> {
    try {
      const typeMap: Record<string, string> = {
        'story': 'originals',
        'cinema': 'media/cinema',
        'content': 'media/contents',
        'podcast': 'podcasts',
        'animation': 'animations',
        'sneak-peek': 'sneak-peeks'
      };

      const endpoint = typeMap[contentType];
      if (!endpoint) {
        throw new Error('Invalid content type');
      }

      await apiRequest(`/${endpoint}/${contentId}/bookmark/`, {
        method: 'POST',
      });
    } catch (error) {
      throw error;
    }
  },

  // Track Watch Progress
  async trackProgress(contentType: string, contentId: string, progress: number): Promise<void> {
    try {
      const typeMap: Record<string, string> = {
        'story': 'originals',
        'cinema': 'media/cinema',
        'content': 'media/content',
        'podcast': 'podcasts',
        'animation': 'animations',
        'sneak-peek': 'sneak-peeks',
        'live-video': 'content'
      };

      const endpoint = typeMap[contentType];
      if (!endpoint) {
        throw new Error('Invalid content type');
      }

      await apiRequest(`/${endpoint}/${contentId}/track-progress/`, {
        method: 'POST',
        body: JSON.stringify({ progress }),
      });
    } catch (error) {
      throw error;
    }
  }
};